import {Suspense, lazy, useCallback, useMemo, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {makeSelectTabOperations} from 'redux/store/auth';
import {NavTabsEnum} from 'utils/constants';

/**
 * Loaded on demand. The composer drags in Quill, the WhatsApp template
 * machinery and a wide batch query; most visits to a wallet or order table
 * never open it, and every page that offers the action would otherwise pay for
 * it on first paint.
 */
const SendUserNotificationModal = lazy(() =>
  import('./SendUserNotificationModal'),
);

/**
 * "Send a notification to this user", wired into a table row.
 *
 * Every page that lists users against something they did — a wallet
 * transaction, a swap, an order, a KYC submission — can offer the same action
 * without rebuilding the composer. The hook owns which user is being notified
 * and renders the modal; the page decides where the trigger lives.
 *
 *   const {canSendNotification, openSendNotification, sendNotificationModal} =
 *     useSendUserNotification();
 *   // …inside a row menu:
 *   onClick: () => openSendNotification(record?.user, {
 *     label: 'Wallet transaction',
 *     reference: record?.referenceId,
 *   })
 *   // …once, next to the table:
 *   {sendNotificationModal}
 *
 * Sending is gated on the notification schedule permission rather than on the
 * host page's own: composing a notification is the notification tab's job no
 * matter which table it is launched from.
 */
export const useSendUserNotification = () => {
  const [target, setTarget] = useState(null);
  const openCount = useRef(0);

  const notificationOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.NotificationSchedule),
  );
  const canSendNotification = Boolean(notificationOperations?.create);

  const openSendNotification = useCallback((user, context, defaults) => {
    openCount.current += 1;
    // Every open gets its own key, so React remounts the modal rather than
    // reusing it. That is what clears the state the modal does not own —
    // notably the WhatsApp template field's internal draft — when the admin
    // moves from one row to the next without closing first.
    setTarget({
      user,
      context,
      // Optional seed values for the composer, so a caller that already knows
      // the language or channels does not make the agent pick them again.
      defaults,
      key: `send-notification-${openCount.current}`,
    });
  }, []);

  const closeSendNotification = useCallback(() => setTarget(null), []);

  // Unmounted while closed, so nothing survives between sends: the next open
  // builds the form from scratch.
  const sendNotificationModal = useMemo(
    () =>
      target ? (
        <Suspense fallback={null}>
          <SendUserNotificationModal
            key={target.key}
            isOpen
            user={target.user}
            context={target.context}
            defaults={target.defaults}
            onClose={closeSendNotification}
          />
        </Suspense>
      ) : null,
    [target, closeSendNotification],
  );

  return {
    canSendNotification,
    openSendNotification,
    closeSendNotification,
    sendNotificationModal,
  };
};

/**
 * The row-menu entry itself, so the five tables that offer this action spell
 * it the same way. Returns `null` when there is nobody to notify — spread it
 * into a menu with `...(item ? [item] : [])`.
 */
export const buildSendNotificationMenuItem = ({
  user,
  context,
  onOpen,
  allowed = true,
}) => {
  if (!allowed || !user?.id) return null;
  return {
    key: 'send-notification',
    label: 'Send Notification',
    onClick: event => {
      event?.domEvent?.stopPropagation?.();
      onOpen(user, context);
    },
  };
};
